import { useCallback, useState } from 'react';

import useToggle from '@lyearn/core/hooks/useToggle';
import { useUserContext } from '@/modules/auth/hooks/useUser';
import { FeedbackResponseInput } from '@/types/schema';

import { EmojiEnum } from '../../components/MoodEmojiList/consts';
import { useCreateFeedbackMutation } from '../../graphql/mutations/generated/createFeedback';
import { useUpdateFeedbackMutation } from '../../graphql/mutations/generated/updateFeedback';
import { FeedbackProps } from '../../types';
import useFeedback from '../useFeedback';

const FEEDBACK_HIDE_DURATION = 1000;

const useFeedbackActions = ({ entityId, entityType, questions }: FeedbackProps) => {
  const user = useUserContext();

  const [feedbackTaken, setFeedbackTaken] = useState(false);
  const [hideAIFeedback, { on }] = useToggle(false);
  const [feedbackId, setFeedbackId] = useState<null | string>(null);
  const [dialogOpen, { toggle }] = useToggle(false);
  const [emojiResponse, setEmojiResponse] = useState<FeedbackResponseInput | null>(null);
  const [, executeCreateFeedback] = useCreateFeedbackMutation();
  const [, executeUpdateFeedback] = useUpdateFeedbackMutation();

  const { feedbackQuestionsObject } = useFeedback({ questions });
  const isRatingOnFiveScale = feedbackQuestionsObject?.feedbackRating?.settings?.ratingScale === 5;

  const onSubmit = useCallback(
    (userFeedback: string) => {
      if (emojiResponse && feedbackId) {
        executeUpdateFeedback({
          input: {
            feedbackId,
            responses: [
              emojiResponse,
              {
                questionId: feedbackQuestionsObject.feedbackDescription.id,
                response: userFeedback,
              },
            ],
          },
        });
      }
    },
    [
      emojiResponse,
      feedbackId,
      executeUpdateFeedback,
      feedbackQuestionsObject.feedbackDescription.id,
    ],
  );

  const onEmojiClick = useCallback(
    async (emoji: EmojiEnum, showDialog: boolean = true) => {
      const response = await executeCreateFeedback({
        input: {
          userId: user.id,
          entityId: entityId,
          entityType: entityType,
          responses: [{ questionId: feedbackQuestionsObject.feedbackRating.id, response: emoji }],
        },
      });

      if (!response.error) {
        const feedbackId = response.data?.createFeedback.feedback?.id;
        if (feedbackId) {
          setFeedbackId(feedbackId);
        }
      }

      setEmojiResponse({
        questionId: feedbackQuestionsObject.feedbackRating.id,
        response: emoji,
      });
      if (showDialog) {
        toggle();
      } else {
        setFeedbackTaken(true);
        setTimeout(() => {
          on();
        }, FEEDBACK_HIDE_DURATION);
      }
    },
    [
      executeCreateFeedback,
      user.id,
      entityId,
      entityType,
      feedbackQuestionsObject.feedbackRating.id,
      toggle,
      on,
    ],
  );

  const onDialogClose = useCallback(() => {
    toggle();
    setFeedbackTaken(true);
    setTimeout(() => {
      on();
    }, FEEDBACK_HIDE_DURATION);
  }, [on, toggle]);

  return {
    emojiResponse,
    feedbackQuestionsObject,
    onEmojiClick,
    onSubmit,
    dialogOpen,
    feedbackTaken,
    onDialogClose,
    hideAIFeedback,
    isRatingOnFiveScale,
  };
};

export default useFeedbackActions;
